// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { SolutionPropositionContentModel } from 'src/app/api/remote-api/.patternmatch.generated/models/solution-proposition-content-model';
import { SolutionPropositionModel } from 'src/app/api/remote-api/.patternmatch.generated/models/solution-proposition-model';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { DeepReadonly } from 'src/app/modules/shared/utils/api-call-state.util';
import { Observe } from 'src/app/modules/shared/utils/as-observable';
import { PatternMatchModelWithId } from '../../services/pattern-match-data/pattern-match-data.service';
import { MouseEventData } from '../pm-chart/pm-chart.component';

type TransformedItem = PatternMatchModelWithId & { count: string };
@Component({
  selector: 'app-pm-table',
  templateUrl: './pm-table.component.html',
  styleUrls: ['./pm-table.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AsyncPipe, TranslateModule, NgClass, NgTemplateOutlet],
})
export class PatternMatchTableComponent implements OnChanges {
  @Input({ required: true }) patternMatches!: DeepReadonly<
    PatternMatchModelWithId[]
  >;
  @Observe('patternMatches') patternMatches$!: Observable<
    typeof this.patternMatches
  >;

  @Input() hoveredBar?: DeepReadonly<MouseEventData>;
  @Input() selectedBar?: DeepReadonly<MouseEventData>;
  @Observe('selectedBar') selectedBar$!: Observable<typeof this.selectedBar>;
  @Input() selectedRow?: PatternMatchModelWithId;

  @Output() rowClicked = new EventEmitter<PatternMatchModelWithId>();

  sortBy$ = new BehaviorSubject<{
    key: keyof TransformedItem;
    ascending: boolean;
  }>({ key: 'count', ascending: false });

  transformedMatches$ = combineLatest({
    matches: this.patternMatches$,
    sortBy: this.sortBy$,
    selectedBar: this.selectedBar$,
  }).pipe(
    map(({ matches, sortBy, selectedBar }) => {
      let filterByDate = (finding: Readonly<{ date: string }>) => !!finding;
      if (selectedBar) {
        const day = moment(selectedBar?.bin.date);
        filterByDate = (finding: Readonly<{ date: string }>) =>
          moment(finding.date).isSame(day, 'day');
        matches = matches.map((match) => ({
          ...match,
          disabled: !match.matchFindings.find(filterByDate),
        }));
      }
      return (
        matches
          .map((match) => ({
            ...match,
            matchFindings: [...(match.matchFindings ?? [])],
            solutionPropositions: (match.solutionPropositions ?? []).map(
              (sp) => ({
                ...sp,
                solutionPropositionContents: [
                  ...(sp.solutionPropositionContents ?? []),
                ],
              }),
            ),
            count: match.matchFindings
              .filter(filterByDate)
              .reduce((a, m) => m.occurrence + a, 0),
          }))
          // sort
          .sort((a, b) => {
            // in case we have a selected bar we sort by selected promisions first!
            if (selectedBar) {
              const aa = a as { disabled?: boolean };
              const bb = b as { disabled?: boolean };
              if (bb.disabled !== aa.disabled) {
                return aa.disabled ? 1 : -1;
              }
            }
            // withing selected ones we sorty by date
            return (
              (sortBy.ascending ? -1 : 1) *
              String(b[sortBy.key]).localeCompare(String(a[sortBy.key]))
            );
          })
      );
    }),
  );

  highlighted = new Set<number>();

  showInstructions = false;
  instructions?: SolutionPropositionContentModel;

  constructor(protected screenLayoutService: ScreenLayoutService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['hoveredBar'] ||
      changes['patternMatches'] ||
      changes['selectedRow'] ||
      changes['selectedBar']
    ) {
      this.computeHighlighted();
    }
  }

  clickRow(item: PatternMatchModelWithId): void {
    this.rowClicked.emit(item);
  }

  toggleSort(key: keyof TransformedItem): void {
    const sort = this.sortBy$.value;
    if (sort.key === key) {
      sort.ascending = !sort.ascending;
    } else {
      sort.key = key;
      sort.ascending = false;
    }
    this.sortBy$.next(sort);
  }

  computeHighlighted(): void {
    this.highlighted.clear();
    const selectedDate = this.selectedBar && moment(this.selectedBar?.bin.date);
    this.patternMatches.forEach((pm) => {
      if (
        undefined !=
          this.hoveredBar?.bin.items.find((i) => i.raw.id === pm.id) ||
        this.selectedRow?.id === pm.id
      ) {
        this.highlighted.add(pm.id);
      }
      if (
        selectedDate &&
        pm.matchFindings.find((finding) =>
          selectedDate.isSame(moment(finding.date), 'day'),
        )
      ) {
        this.highlighted.add(pm.id);
      }
    });
  }

  openSolution(
    event: MouseEvent,
    solution: DeepReadonly<SolutionPropositionModel>,
    content: DeepReadonly<SolutionPropositionContentModel>,
  ): void {
    if (solution.type === 'instructionText') {
      this.showInstructions = true;
      this.instructions = content;
      event.preventDefault();
    }
    // else just open window
    event.stopImmediatePropagation();
  }
}
